import { useState, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Upload, X, FileText, Loader2 } from 'lucide-react';
import TextareaAutosize from 'react-textarea-autosize';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { assessInputQuality } from '@/lib/intake/assessInputQuality';
import { detectLiveSignals } from '@/lib/intake/detectLiveSignals';
import { LiveSignalBar } from '@/components/intake/LiveSignalBar';

export interface UploadedFile {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  status: 'uploading' | 'uploaded' | 'parsing' | 'parsed' | 'error';
  chunkCount?: number;
  storagePath: string;
  errorMessage?: string;
}

interface ContextIntakeScreenProps {
  onSubmit: (content: string) => void;
  isExtracting?: boolean;
  initialValue?: string;
  uploadedFiles: UploadedFile[];
  onFilesChange: (files: UploadedFile[] | ((prev: UploadedFile[]) => UploadedFile[])) => void;
  workspaceId: string;
  onSwitchToClassic?: () => void;
}

const ACCEPTED_TYPES = [
  'application/pdf',
  'text/plain',
  'text/markdown',
  'text/x-markdown',
];

const ACCEPTED_EXTENSIONS = ['.pdf', '.txt', '.md'];
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_FILES = 2;

const EXAMPLE_TEXT = `Wir stehen vor der Frage, ob wir unseren Vertrieb jetzt direkt starten und auf Pilotkunden im Mittelstand zugehen sollen, oder ob wir erst unsere Positionierung und unser Pricing schärfen sollten, bevor wir aktiv werden.

Wir haben ein SaaS-Produkt für mittelständische Unternehmen, das Entscheidungsprozesse strukturiert. Die ersten 3 Beta-Nutzer haben positives Feedback gegeben, aber wir sind uns unsicher über die richtige Preisstrategie. Unser Runway reicht noch 6 Monate.

Bisherige Versuche: Wir hatten 2 Kaltakquise-Kampagnen, die wenig Response gebracht haben. Jetzt überlegen wir, ob das am Timing lag oder am Messaging.`;

function getFileExtension(name: string): string {
  return name.slice(name.lastIndexOf('.')).toLowerCase();
}

function isValidFileType(file: File): boolean {
  if (ACCEPTED_TYPES.includes(file.type)) return true;
  const ext = getFileExtension(file.name);
  return ACCEPTED_EXTENSIONS.includes(ext);
}

function getCTALabel(level: string, wordCount: number): string {
  if (wordCount >= 50 && level === 'strong') return 'Analyse starten';
  if (level === 'adequate') return 'Struktur erzeugen';
  return 'Entscheidung strukturieren';
}

export const ContextIntakeScreen = ({
  onSubmit,
  isExtracting,
  initialValue,
  uploadedFiles,
  onFilesChange,
  workspaceId,
  onSwitchToClassic,
}: ContextIntakeScreenProps) => {
  const [content, setContent] = useState(initialValue || '');
  const [showExample, setShowExample] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isValid = content.trim().length >= 50;
  const hasContent = content.trim().length > 0;
  
  const inputQuality = useMemo(() => assessInputQuality(content), [content]);
  const liveSignals = useMemo(() => detectLiveSignals(content), [content]);
  const ctaLabel = useMemo(() => getCTALabel(inputQuality.level, inputQuality.wordCount), [inputQuality]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (fileInputRef.current) fileInputRef.current.value = '';

    const remainingSlots = MAX_FILES - uploadedFiles.length;
    if (remainingSlots <= 0) {
      toast({ title: `Maximal ${MAX_FILES} Dateien`, variant: 'destructive' });
      return;
    }

    const toUpload = files.slice(0, remainingSlots);

    for (const file of toUpload) {
      if (!isValidFileType(file)) {
        toast({ title: 'Dateityp nicht unterstützt', description: 'Nur PDF, TXT und MD werden akzeptiert.', variant: 'destructive' });
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast({ title: 'Datei zu groß', description: 'Maximal 10 MB pro Datei.', variant: 'destructive' });
        continue;
      }

      const tempId = crypto.randomUUID();
      const storagePath = `${workspaceId}/${tempId}/${file.name}`;
      const newFile: UploadedFile = {
        id: tempId, fileName: file.name, fileType: file.type || 'application/octet-stream',
        fileSize: file.size, status: 'uploading', storagePath,
      };
      onFilesChange(prev => [...prev, newFile]);

      try {
        const { error: uploadError } = await supabase.storage.from('session-intake-files').upload(storagePath, file, { contentType: file.type });
        if (uploadError) throw uploadError;
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');
        const { data: docRecord, error: dbError } = await supabase
          .from('session_intake_documents' as any)
          .insert({ workspace_id: workspaceId, uploaded_by: user.id, file_name: file.name, file_type: file.type || 'application/octet-stream', file_size: file.size, storage_path: storagePath, parse_status: 'pending' })
          .select('id').single();
        if (dbError) throw dbError;
        onFilesChange(prev => prev.map(f => f.id === tempId ? { ...f, id: (docRecord as any).id, status: 'uploaded' as const } : f));
      } catch (err: any) {
        console.error('Upload error:', err);
        onFilesChange(prev => prev.map(f => f.id === tempId ? { ...f, status: 'error' as const, errorMessage: err?.message || 'Upload fehlgeschlagen' } : f));
      }
    }
  };

  const handleRemoveFile = async (fileId: string) => {
    const file = uploadedFiles.find(f => f.id === fileId);
    if (file) {
      try {
        await supabase.storage.from('session-intake-files').remove([file.storagePath]);
        await supabase.from('session_intake_documents' as any).delete().eq('id', fileId);
      } catch { /* Best effort */ }
    }
    onFilesChange(prev => prev.filter(f => f.id !== fileId));
  };

  return (
    <div
      className="qf-intake-bg w-full flex flex-col items-center pt-[120px] pb-20 px-6"
      style={{
        background: 'radial-gradient(circle at 50% 40%, rgba(20,30,50,0.04), transparent 60%)',
      }}
    >
      <div className="w-full max-w-[960px] flex flex-col items-center">

        {/* ── Brand Anchor ── */}
        <span className="text-[11px] tracking-[0.12em] uppercase text-muted-foreground/50 mb-6">
          Quentari Decision Engine
        </span>

        {/* ── Headline ── */}
        <h2 className="text-[32px] md:text-[40px] font-semibold leading-[1.2] tracking-tight text-foreground text-center max-w-[720px]">
          Bring dein{' '}
          <span
            className="relative"
            style={{
              background: 'linear-gradient(to bottom, transparent 60%, hsl(var(--primary) / 0.12) 60%)',
            }}
          >
            Entscheidungschaos
          </span>
          {' '}hier rein.
        </h2>

        {/* ── Subline ── */}
        <p className="mt-4 text-base text-muted-foreground leading-relaxed text-center max-w-[600px]">
          Optionen, Zweifel, Ziele, Risiken.
          <br />
          Unsortiert reicht.
          <br />
          Wir machen daraus eine klare Entscheidungsstruktur.
        </p>

        {/* ── Permission line ── */}
        <p className="mt-2 text-[13px] text-muted-foreground/40 text-center">
          Keine Struktur nötig. Keine Vorbereitung.
        </p>

        {/* ══════ Input Field ══════ */}
        <div className="mt-10 w-full max-w-[820px]">
          <div
            className={cn(
              'qf-effect-shell',
              isFocused && 'qf-focus',
              isHovered && !isFocused && 'qf-hover',
              hasContent && 'qf-has-content',
            )}
            onMouseEnter={() => !isFocused && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className="qf-breath" />
            <div className="qf-field-shell">
              <div className="qf-wave" />
              <div className="qf-edge" />
              <div className="qf-inner px-6 pt-6 pb-5">
                {/* System presence + field label */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] tracking-wider uppercase text-muted-foreground/35">
                    Analysefläche
                  </span>
                  <span className={cn(
                    'text-[11px] tracking-wider uppercase transition-colors duration-500',
                    hasContent ? 'text-primary/45' : 'text-muted-foreground/25',
                  )}>
                    {hasContent ? 'Struktur wird vorbereitet' : 'System bereit'}
                  </span>
                </div>

                <div className="relative min-h-[180px]">
                  <TextareaAutosize
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    onFocus={() => { setIsFocused(true); setIsHovered(false); }}
                    onBlur={() => setIsFocused(false)}
                    minRows={7}
                    maxRows={20}
                    placeholder={"Ich schwanke gerade zwischen zwei Richtungen…\n\nOption A wäre…\nOption B wäre…\n\nDas Problem ist…\n\nWenn ich falsch entscheide, passiert…\n\nMein Ziel ist…"}
                    className="w-full bg-transparent border-none outline-none resize-none text-base leading-relaxed text-foreground placeholder:text-muted-foreground/40 focus-visible:outline-none"
                    style={{ caretColor: 'hsl(var(--primary))' }}
                  />
                </div>
                {/* Bottom meta */}
                <div className="flex items-center justify-between mt-2 h-7">
                  <span className={cn(
                    'text-xs transition-opacity duration-300 text-muted-foreground/50',
                    hasContent ? 'opacity-100' : 'opacity-0',
                  )}>
                    Du musst nichts perfekt formulieren. Fehlende Punkte klären wir im nächsten Schritt.
                  </span>
                  {content.length > 0 && content.length < 50 && (
                    <span className="text-[11px] text-muted-foreground/40 tabular-nums font-mono">
                      {content.length}/50
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Live Signal Bar */}
          <LiveSignalBar signals={liveSignals} />
        </div>

        {/* Uploaded files */}
        {uploadedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center mt-4">
            {uploadedFiles.map((f) => (
              <div
                key={f.id}
                className={cn(
                  'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs transition-colors',
                  f.status === 'error'
                    ? 'border-destructive/30 bg-destructive/5 text-destructive'
                    : f.status === 'uploading'
                      ? 'border-border bg-muted/30 text-muted-foreground'
                      : 'border-border/50 bg-card text-foreground/80',
                )}
              >
                {f.status === 'uploading' ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
                <span className="max-w-[150px] truncate">{f.fileName}</span>
                {f.status === 'error' && <span className="text-[10px] text-destructive/70">Fehler</span>}
                <button onClick={() => handleRemoveFile(f.id)} className="ml-0.5 p-0.5 rounded hover:bg-muted transition-colors" aria-label="Datei entfernen">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ── CTA area ── */}
        <div className="mt-8 flex flex-col items-center">
          {/* Quality guidance */}
          {inputQuality.guidance && (
            <span className={cn(
              'text-[11px] font-medium tracking-wide animate-fade-in transition-colors duration-300 mb-3',
              inputQuality.level === 'strong'
                ? 'text-foreground/35 uppercase'
                : inputQuality.level === 'adequate'
                  ? 'text-foreground/40'
                  : 'text-amber-600/60 dark:text-amber-400/50',
            )}>
              {inputQuality.guidance}
            </span>
          )}

          <button
            onClick={() => onSubmit(content)}
            disabled={!isValid || uploadedFiles.some(f => f.status === 'uploading')}
            className={cn(
              'inline-flex items-center gap-2 rounded-[10px] px-[22px] py-[14px] text-[15px] font-medium text-primary-foreground transition-all duration-200',
              isValid
                ? 'bg-gradient-to-b from-foreground/80 to-foreground/90 shadow-[0_10px_30px_rgba(20,30,60,0.2)] hover:-translate-y-px hover:shadow-[0_16px_40px_rgba(20,30,60,0.25)] active:translate-y-0 active:shadow-[0_4px_10px_rgba(20,30,60,0.15)]'
                : 'bg-foreground/85 opacity-60 cursor-not-allowed',
            )}
          >
            {ctaLabel}
          </button>

          {/* System hint — branded */}
          <p className="mt-3 text-[13px] text-muted-foreground text-center max-w-md">
            Quentari extrahiert Optionen · Zielkonflikte werden sichtbar · Risiken und Annahmen identifiziert · ~60s
          </p>

          {/* Secondary actions */}
          <div className="flex items-center gap-4 mt-4">
            <input ref={fileInputRef} type="file" accept=".pdf,.txt,.md" multiple className="hidden" onChange={handleFileSelect} />

            {uploadedFiles.length < MAX_FILES ? (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-xs text-muted-foreground/50 hover:text-muted-foreground/70 transition-colors flex items-center gap-1.5"
              >
                <Upload className="h-3 w-3" />
                Dokument hinzufügen
              </button>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="text-xs text-muted-foreground/20 flex items-center gap-1 cursor-not-allowed">
                      <Upload className="h-3 w-3" /> Datei
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top"><p className="text-xs">Maximal {MAX_FILES} Dateien</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            <button
              onClick={() => setShowExample(!showExample)}
              className="text-xs text-muted-foreground/50 hover:text-muted-foreground/70 transition-colors flex items-center gap-1"
            >
              {showExample ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              Beispiel
            </button>

            {onSwitchToClassic && (
              <button
                onClick={onSwitchToClassic}
                className="text-xs text-muted-foreground/30 hover:text-muted-foreground/50 transition-colors"
              >
                Klassischer Modus
              </button>
            )}
          </div>
        </div>

        {/* Example */}
        {showExample && (
          <div className="rounded-xl border border-border/40 bg-card/50 p-5 space-y-3 animate-fade-in max-w-xl mx-auto mt-6">
            <p className="text-xs text-foreground/60 leading-relaxed whitespace-pre-line">{EXAMPLE_TEXT}</p>
            <Button variant="outline" size="sm" onClick={() => { setContent(EXAMPLE_TEXT); setShowExample(false); }}>
              Übernehmen
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
